import type { ReactNode } from "react";
import AddIcon from "@mui/icons-material/Add";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useResizeObserverRef } from "@wojtekmaj/react-hooks";
import { useTranslation } from "react-i18next";
import type { Except, Promisable } from "type-fest";

import type {
  EndingRefusal,
  GuestOnCase,
  PersonInvolvement,
  PersonInvolvementId,
  PersonInvolvementKind,
} from "@thalamos/haven-common";
import {
  hasInvolvementEnded,
  isInvolvementKindHeldByOneAtMost,
  isInvolvementKindSetAtCreation,
  isInvolvementLive,
  isInvolvementStartAwaitingDesignatedAssessor,
} from "@thalamos/haven-common";

import {
  ACTION_TABLE_MIN_WIDTH,
  ActionTableSection,
} from "../ActionTable/ActionTable.js";
import { CaseGuests, type CaseGuestsProps } from "../CaseGuests/CaseGuests.js";

const CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" });

export type PartyKind = PersonInvolvement["party"]["kind"];

export type CaseInvolvementActor = "responsible_user" | "created_by";

/**
 * Changing who is involved, for a reader who may. Absent for everybody else,
 * which is what makes the tab read-only by default.
 *
 * Each change is offered only where its handler is given and the actor may make
 * it.
 */
export type CaseInvolvementManagement = {
  actor: CaseInvolvementActor;
  onAdd?: (
    kind: PersonInvolvementKind,
    partyKind: PartyKind,
  ) => Promisable<void>;
  onReplace?: (involvement: PersonInvolvement) => Promisable<void>;
  /**
   * The roles `onReplace` can hand on. Replace is offered where the actor may
   * replace and the role is listed; every role the actor may replace when
   * absent.
   */
  replaces?: readonly PersonInvolvementKind[];
  onEnd?: (involvement: PersonInvolvement) => Promisable<void>;
  /**
   * Why a holder may not be ended now, from `endingRefusalOf`. End is still
   * shown where the actor may end the role, but disabled, with the reason
   * beneath.
   */
  endingRefusal?: (involvement: PersonInvolvement) => EndingRefusal | undefined;
};

/**
 * Every role, shown whether or not anybody holds it, in the order a reader
 * looks for them: accountable parts first, oversight last.
 *
 * TODO(haven-involvements): add the Coordination role to the organisations
 * once the record has an involvement kind for it.
 */
export const ORGANISATION_ROLES: readonly PersonInvolvementKind[] = [
  "created_by",
  "duty_of_care",
  "placement",
  "viewer",
];

export const PERSON_ROLES: readonly PersonInvolvementKind[] = [
  "responsible_user",
  "designated_assessor",
  "approver",
  "assessor",
  "viewer",
];

/**
 * What each actor may change. The tables are deliberately not mirror images:
 * whoever runs the case may change every part, and the body that opened the
 * record may only allocate who runs a case nobody runs. Nobody is offered End on
 * whoever runs the case: the role is replaced instead, so it is not left
 * empty.
 */
const MAY_ADD: Record<
  CaseInvolvementActor,
  (kind: PersonInvolvementKind) => boolean
> = {
  responsible_user: () => true,
  created_by: (kind) => kind === "responsible_user",
};

const MAY_REPLACE: Record<
  CaseInvolvementActor,
  (kind: PersonInvolvementKind) => boolean
> = {
  responsible_user: () => true,
  created_by: () => false,
};

const MAY_END: Record<
  CaseInvolvementActor,
  (kind: PersonInvolvementKind) => boolean
> = {
  responsible_user: (kind) => kind !== "responsible_user",
  created_by: () => false,
};

/** Whether the actor may Add on a role's own row: a one-holder role only while empty. */
export const mayAdd = (
  actor: CaseInvolvementActor | undefined,
  kind: PersonInvolvementKind,
  holderCount: number,
): boolean =>
  actor !== undefined &&
  !isInvolvementKindSetAtCreation(kind) &&
  !(isInvolvementKindHeldByOneAtMost(kind) && holderCount > 0) &&
  MAY_ADD[actor](kind);

/** Whether the actor may Replace a holder, which only a one-holder role has. */
export const mayReplace = (
  actor: CaseInvolvementActor | undefined,
  kind: PersonInvolvementKind,
): boolean =>
  actor !== undefined &&
  !isInvolvementKindSetAtCreation(kind) &&
  isInvolvementKindHeldByOneAtMost(kind) &&
  MAY_REPLACE[actor](kind);

export const mayEnd = (
  actor: CaseInvolvementActor | undefined,
  kind: PersonInvolvementKind,
): boolean =>
  actor !== undefined &&
  !isInvolvementKindSetAtCreation(kind) &&
  MAY_END[actor](kind);

/** Who may hold a role, where the role says more than how many. */
const HOLDER_DETAIL: Partial<
  Record<
    PersonInvolvementKind,
    "localAuthority" | "careSetting" | "notTheDesignatedAssessor"
  >
> = {
  duty_of_care: "localAuthority",
  placement: "careSetting",
  approver: "notTheDesignatedAssessor",
};

const byStart = (a: PersonInvolvement, b: PersonInvolvement) =>
  a.startedAt.getTime() - b.startedAt.getTime();

/**
 * When a holder stops conveying anything: an ending still to come or an
 * expiry, whichever is first.
 */
const untilOf = ({ endedAt, expiresAt }: PersonInvolvement): Date | null =>
  endedAt === null || (expiresAt !== null && expiresAt < endedAt)
    ? expiresAt
    : endedAt;

const onBehalfOf = (
  party: PersonInvolvement["party"],
  guest: string,
): string | null =>
  party.kind === "user" ? (party.onBehalfOfOrganisationName ?? guest) : null;

/** The role's name, how many may hold it, and whether the record needs it. */
const RoleHeading = ({
  kind,
  flagged,
  muted,
}: {
  kind: PersonInvolvementKind;
  flagged: boolean;
  muted: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Typography
        variant="body2"
        fontWeight="medium"
        color={muted ? "text.secondary" : undefined}
      >
        {t(`access.kind.${kind}`)}
      </Typography>
      <Typography variant="caption" display="block" color="text.secondary">
        {isInvolvementKindSetAtCreation(kind)
          ? t("access.caseInvolvement.setAtCreation")
          : [
              isInvolvementKindHeldByOneAtMost(kind)
                ? t("access.caseInvolvement.one")
                : t("access.caseInvolvement.anyNumber"),
              ...(HOLDER_DETAIL[kind] === undefined
                ? []
                : [t(`access.caseInvolvement.holder.${HOLDER_DETAIL[kind]}`)]),
            ].join(" · ")}
      </Typography>
      {flagged ? (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ mt: 0.5 }}
        >
          {/* The warning colour stays on the icon: as text on white it falls
              short of the contrast body text needs. */}
          <WarningAmberOutlinedIcon
            fontSize="inherit"
            color="warning"
            aria-hidden
          />
          <Typography variant="caption">
            {t("access.caseInvolvement.neededToStart")}
          </Typography>
        </Stack>
      ) : null}
    </>
  );
};

const ExpiredChip = () => {
  const { t } = useTranslation();

  return (
    <Chip
      size="small"
      variant="outlined"
      label={t("access.caseInvolvement.expired")}
      sx={{ ml: 1 }}
    />
  );
};

const CannotChange = () => {
  const { t } = useTranslation();

  return (
    <Typography variant="caption" color="text.secondary">
      {t("access.caseInvolvement.cannotChange")}
    </Typography>
  );
};

/**
 * What the role itself offers: Add where it may take another holder, a note
 * where it must wait for a designated assessor first, or a note that who
 * opened the record can't be changed.
 */
const RoleAction = ({
  kind,
  partyKind,
  holderCount,
  awaitingDesignation,
  management,
}: {
  kind: PersonInvolvementKind;
  partyKind: PartyKind;
  holderCount: number;
  awaitingDesignation: boolean;
  management: CaseInvolvementManagement | undefined;
}) => {
  const { t } = useTranslation();

  if (isInvolvementKindSetAtCreation(kind)) {
    return management === undefined ? null : <CannotChange />;
  }

  const onAdd = management?.onAdd;

  if (onAdd === undefined || !mayAdd(management?.actor, kind, holderCount)) {
    return null;
  }

  return awaitingDesignation ? (
    <Typography variant="caption" color="text.secondary">
      {t("access.caseInvolvement.designateFirst")}
    </Typography>
  ) : (
    <Button
      size="small"
      variant="contained"
      startIcon={<AddIcon />}
      aria-label={t(`access.caseInvolvement.addLabel.${partyKind}`, {
        role: t(`access.kind.${kind}`),
      })}
      onClick={() => {
        void onAdd(kind, partyKind);
      }}
      sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
    >
      {t(`access.caseInvolvement.add.${partyKind}`)}
    </Button>
  );
};

const HolderActions = ({
  involvement,
  management,
}: {
  involvement: PersonInvolvement;
  management: CaseInvolvementManagement | undefined;
}) => {
  const { t } = useTranslation();
  const { party, kind } = involvement;
  const labels = { name: party.name, role: t(`access.kind.${kind}`) };
  const onReplace = management?.onReplace;
  const onEnd = management?.onEnd;
  const refusal = management?.endingRefusal?.(involvement);
  const endButton =
    onEnd === undefined || !mayEnd(management?.actor, kind) ? null : (
      <Button
        size="small"
        variant="outlined"
        color="error"
        disabled={refusal !== undefined}
        aria-label={t("access.caseInvolvement.endLabel", labels)}
        onClick={() => {
          void onEnd(involvement);
        }}
      >
        {t("access.caseInvolvement.end")}
      </Button>
    );
  // The reason sits beneath only where there is one, so an End that can be
  // pressed lines up with every other action at the row's right-hand end.
  const end =
    endButton === null || refusal === undefined ? (
      endButton
    ) : (
      <Stack spacing={0.5} alignItems="flex-end">
        {endButton}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ maxWidth: 220, textAlign: "right", whiteSpace: "normal" }}
        >
          {t(`access.caseInvolvement.endRefused.${refusal}`)}
        </Typography>
      </Stack>
    );

  return (
    <>
      {onReplace !== undefined &&
      mayReplace(management?.actor, kind) &&
      (management?.replaces?.includes(kind) ?? true) ? (
        <Button
          size="small"
          variant="outlined"
          aria-label={t("access.caseInvolvement.replaceLabel", labels)}
          onClick={() => {
            void onReplace(involvement);
          }}
        >
          {t("access.caseInvolvement.replace")}
        </Button>
      ) : null}
      {end}
    </>
  );
};

const ActionsCell = ({ children }: { children: ReactNode }) => (
  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
    <Stack direction="row" spacing={1} justifyContent="flex-end">
      {children}
    </Stack>
  </TableCell>
);

type RoleProps = {
  kind: PersonInvolvementKind;
  partyKind: PartyKind;
  holders: PersonInvolvement[];
  flagged: boolean;
  /** Whether Add must wait, per {@link isInvolvementStartAwaitingDesignatedAssessor}. */
  awaitingDesignation: boolean;
  management: CaseInvolvementManagement | undefined;
};

/**
 * Read by assistive technology but not drawn. Lengths are pixel strings
 * because `sx` reads a width or height from 0 to 1 as a fraction (so
 * `width: 1` is 100%) and a bare margin as theme spacing.
 */
const VISUALLY_HIDDEN = {
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  padding: 0,
  border: 0,
  overflow: "hidden",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap",
} as const;

/**
 * Drops the rule beneath a row, so a role and its holders' rows read as one
 * block, ruled off only after the last: the holders' role cells are blank to
 * the eye.
 */
const RUN_ON = { "& > td, & > th": { borderBottom: 0 } } as const;

const emptyRole = (kind: PersonInvolvementKind) =>
  isInvolvementKindHeldByOneAtMost(kind)
    ? "access.caseInvolvement.notAssigned"
    : "access.caseInvolvement.none";

/**
 * One role as table rows. A one-holder role that is held is a single row;
 * otherwise the role has a row of its own, carrying Add, with each holder on a
 * row beneath it.
 */
const RoleRows = ({
  kind,
  partyKind,
  holders,
  flagged,
  awaitingDesignation,
  management,
}: RoleProps) => {
  const { t } = useTranslation();
  const muted = isInvolvementKindSetAtCreation(kind);
  const roleCell = (
    <TableCell component="th" scope="row">
      <RoleHeading kind={kind} flagged={flagged} muted={muted} />
    </TableCell>
  );
  // Every holder row is headed by its role, hidden where the role's own row
  // already shows it, so a screen reader names the role on each row it reads.
  const repeatedRoleCell = (
    <TableCell component="th" scope="row">
      <Box component="span" sx={VISUALLY_HIDDEN}>
        {t(`access.kind.${kind}`)}
      </Box>
    </TableCell>
  );
  const holderRows = (first: ReactNode) =>
    holders.map((involvement, index) => {
      const until = untilOf(involvement);

      return (
        <TableRow
          key={involvement.id}
          sx={[
            muted && { "& td": { color: "text.secondary" } },
            index < holders.length - 1 && RUN_ON,
          ]}
        >
          {index === 0 ? first : repeatedRoleCell}
          <TableCell>{involvement.party.name}</TableCell>
          {partyKind === "user" ? (
            <TableCell>
              {onBehalfOf(involvement.party, t("access.caseInvolvement.guest"))}
            </TableCell>
          ) : null}
          <TableCell sx={{ whiteSpace: "nowrap" }}>
            {CALENDAR_DATE.format(involvement.startedAt)}
          </TableCell>
          <TableCell sx={{ whiteSpace: "nowrap" }}>
            {until === null ? null : CALENDAR_DATE.format(until)}
            {isInvolvementLive(involvement) ? null : <ExpiredChip />}
          </TableCell>
          <ActionsCell>
            {muted && management !== undefined ? <CannotChange /> : null}
            <HolderActions involvement={involvement} management={management} />
          </ActionsCell>
        </TableRow>
      );
    });

  if (isInvolvementKindHeldByOneAtMost(kind) && holders.length > 0) {
    return holderRows(roleCell);
  }

  return (
    <>
      <TableRow sx={holders.length > 0 ? RUN_ON : undefined}>
        {roleCell}
        <TableCell colSpan={partyKind === "user" ? 4 : 3}>
          {holders.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t(emptyRole(kind))}
            </Typography>
          ) : null}
        </TableCell>
        <ActionsCell>
          <RoleAction
            kind={kind}
            partyKind={partyKind}
            holderCount={holders.length}
            awaitingDesignation={awaitingDesignation}
            management={management}
          />
        </ActionsCell>
      </TableRow>
      {holderRows(repeatedRoleCell)}
    </>
  );
};

/**
 * One role stacked for a narrow width, in place of {@link RoleRows}: the role
 * and its action, then each holder with their actions at the right.
 */
const RoleBlock = ({
  kind,
  partyKind,
  holders,
  flagged,
  awaitingDesignation,
  management,
}: RoleProps) => {
  const { t } = useTranslation();
  const muted = isInvolvementKindSetAtCreation(kind);

  return (
    <Stack spacing={1} sx={{ py: 1.5 }}>
      <Stack
        direction="row"
        spacing={1}
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box sx={{ minWidth: 0 }}>
          <RoleHeading kind={kind} flagged={flagged} muted={muted} />
        </Box>
        <RoleAction
          kind={kind}
          partyKind={partyKind}
          holderCount={holders.length}
          awaitingDesignation={awaitingDesignation}
          management={management}
        />
      </Stack>

      {holders.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t(emptyRole(kind))}
        </Typography>
      ) : null}

      {holders.map((involvement) => {
        const behalf = onBehalfOf(
          involvement.party,
          t("access.caseInvolvement.guest"),
        );
        const until = untilOf(involvement);
        const dates = [
          t("access.caseInvolvement.sinceDate", {
            date: CALENDAR_DATE.format(involvement.startedAt),
          }),
          until === null
            ? null
            : t("access.caseInvolvement.untilDate", {
                date: CALENDAR_DATE.format(until),
              }),
        ].filter((part) => part !== null);

        return (
          <Stack
            key={involvement.id}
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            justifyContent="space-between"
            alignItems="center"
            sx={{ pl: 1.5, borderLeft: 2, borderColor: "divider" }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                color={muted ? "text.secondary" : undefined}
              >
                {involvement.party.name}
              </Typography>
              {behalf === null ? null : (
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  {behalf}
                </Typography>
              )}
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
              >
                {dates.join(" · ")}
              </Typography>
              {isInvolvementLive(involvement) ? null : <ExpiredChip />}
            </Box>
            {/* The auto margin keeps the actions at the right when they wrap
                onto a line of their own, where space-between would not. */}
            <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
              <HolderActions
                involvement={involvement}
                management={management}
              />
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};

const RoleGroup = ({
  partyKind,
  involvements,
  isListedUnderGuests,
  missing,
  management,
  stacked,
}: {
  partyKind: PartyKind;
  involvements: PersonInvolvement[];
  /** Whether an involvement is listed under Guests, and so left out here. */
  isListedUnderGuests: (involvement: PersonInvolvement) => boolean;
  missing: readonly PersonInvolvementKind[];
  management: CaseInvolvementManagement | undefined;
  stacked: boolean;
}) => {
  const { t } = useTranslation();
  const title =
    partyKind === "user" ? t("access.users") : t("access.organisations");
  const roles = (partyKind === "user" ? PERSON_ROLES : ORGANISATION_ROLES).map(
    (kind) => {
      const holders = involvements
        .filter(
          (involvement) =>
            involvement.kind === kind &&
            involvement.party.kind === partyKind &&
            !hasInvolvementEnded(involvement) &&
            !isListedUnderGuests(involvement),
        )
        .sort(byStart);

      return {
        kind,
        partyKind,
        holders,
        flagged: missing.includes(kind),
        awaitingDesignation: isInvolvementStartAwaitingDesignatedAssessor(
          kind,
          involvements,
        ),
        management,
      };
    },
  );

  if (stacked) {
    return (
      <Stack component="section" aria-label={title}>
        <Typography variant="subtitle1" component="h3" sx={{ mb: 0.5 }}>
          {title}
        </Typography>
        <Divider />
        <Stack divider={<Divider />}>
          {roles.map((role) => (
            <RoleBlock key={role.kind} {...role} />
          ))}
        </Stack>
      </Stack>
    );
  }

  return (
    <ActionTableSection
      title={title}
      columns={[
        t("access.caseInvolvement.column.role"),
        t("access.caseInvolvement.column.heldBy"),
        ...(partyKind === "user"
          ? [t("access.caseInvolvement.column.onBehalfOf")]
          : []),
        t("access.caseInvolvement.column.since"),
        t("access.caseInvolvement.column.until"),
      ]}
      actionsLabel={t("access.caseInvolvement.column.actions")}
    >
      {roles.map((role) => (
        <RoleRows key={role.kind} {...role} />
      ))}
    </ActionTableSection>
  );
};

/**
 * Whether an involvement belongs under Guests rather than People. Until the
 * guests are read, or where they could not be, every involvement held for no
 * organisation is held back, since any of them may be a guest whose access
 * People's End would not end properly; once read, only the guests listed are.
 */
const listedUnderGuests = (
  guests: readonly GuestOnCase[] | undefined,
): ((involvement: PersonInvolvement) => boolean) => {
  if (guests === undefined) {
    return ({ party }) =>
      party.kind === "user" && party.onBehalfOfOrganisationId === null;
  }

  const listed = new Set<PersonInvolvementId>(
    guests.map(({ involvementId }) => involvementId),
  );

  return ({ id }) => listed.has(id);
};

export type CaseInvolvementProps = {
  /** Undefined until the list has been read, which is not the same as empty. */
  involvements: PersonInvolvement[] | undefined;
  failed: boolean;
  /**
   * The roles the record still needs before an assessment can start, each
   * flagged as given — an expired holder conveys nothing, so its role can be
   * shown held and still be needed.
   */
  missing?: readonly PersonInvolvementKind[];
  management?: CaseInvolvementManagement | undefined;
  /**
   * The guests given a link to this person, listed in a section of
   * their own after People rather than among the roles. Absent where the page
   * lists no guests.
   */
  guests?: Except<CaseGuestsProps, "stacked"> | undefined;
};

/**
 * Who is involved with one person, role by role, and the guests given
 * a link to them, in a section of their own.
 *
 * Every role is listed whether or not it is held, so an empty one reads as a
 * gap to fill rather than going unnoticed. Ended involvements are left out:
 * they are no longer part of the case. One whose ending is still to come is
 * shown with the day it ends: until then it is still part of the case. An
 * expired one is shown, marked as expired: it conveys nothing, but it holds its
 * role until somebody ends it, so it has to stay where it can be ended or
 * replaced.
 */
export const CaseInvolvement = ({
  involvements,
  failed,
  missing = [],
  management,
  guests,
}: CaseInvolvementProps) => {
  const { t } = useTranslation();
  const [ref, entry] = useResizeObserverRef<HTMLDivElement>();
  // Judged by the component's own width rather than the viewport's, so it also
  // stacks when laid out in a narrow container. Unmeasured reads as wide.
  const width = entry?.contentRect.width;
  const stacked = width !== undefined && width < ACTION_TABLE_MIN_WIDTH;
  const isListedUnderGuests =
    guests === undefined ? () => false : listedUnderGuests(guests.guests);
  return (
    <Paper ref={ref} variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack spacing={3}>
        <Typography variant="h6" component="h2">
          {t("access.caseInvolvement.title")}
        </Typography>

        {/* No roles on a failed read: every one would say "Not assigned",
            which is a claim about the child the page cannot make. */}
        {failed ? (
          <Alert severity="error">{t("access.loadFailed")}</Alert>
        ) : involvements === undefined ? (
          <Typography variant="body2" color="text.secondary">
            {t("common.loading")}
          </Typography>
        ) : (
          <>
            <RoleGroup
              partyKind="organisation"
              involvements={involvements}
              isListedUnderGuests={isListedUnderGuests}
              missing={missing}
              management={management}
              stacked={stacked}
            />
            <RoleGroup
              partyKind="user"
              involvements={involvements}
              isListedUnderGuests={isListedUnderGuests}
              missing={missing}
              management={management}
              stacked={stacked}
            />
            {guests === undefined ? null : (
              <CaseGuests
                guests={guests.guests}
                failed={guests.failed}
                actions={guests.actions}
                stacked={stacked}
              />
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
};
